/**
 * Performance Metric Manager – QMS Phase 3
 * List, create, edit performance metrics linked to standards.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, AlertTriangle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { performanceMetricService, METRIC_TYPES, UNITS, FREQUENCIES, TRENDS, DATA_SOURCES } from '../../services/performanceMetricService';
import processService from '../../services/processService';
import './PerformanceMetricManager.css';

const PerformanceMetricManager = ({ organizationId, userId }) => {
  const { t } = useTranslation(['qms', 'dashboards']);
  const [metrics, setMetrics] = useState([]);
  const [standards, setStandards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [processFilter, setProcessFilter] = useState('');
  const [form, setForm] = useState({
    processId: '',
    name: '',
    metricType: 'operational',
    targetValue: '',
    targetOperator: '>=',
    unit: 'percentage',
    measurementFrequency: 'monthly',
    dataSource: 'manual',
    currentValue: '',
    lastMeasuredAt: '',
    trend: 'stable',
    notes: ''
  });

  const loadMetrics = async () => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    try {
      const opts = processFilter ? { processId: processFilter } : {};
      const data = await performanceMetricService.getByOrganization(organizationId, opts);
      setMetrics(data);
    } catch (err) {
      console.error('Load metrics:', err);
      setError(err.message);
      setMetrics([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStandards = async () => {
    if (!organizationId) return;
    try {
      const data = await processService.getProcesses(organizationId);
      setStandards(data || []);
    } catch (_) {}
  };

  useEffect(() => {
    loadStandards();
  }, [organizationId]);

  useEffect(() => {
    loadMetrics();
  }, [organizationId, processFilter]);

  const resetForm = () => {
    setForm({
      processId: '',
      name: '',
      metricType: 'operational',
      targetValue: '',
      targetOperator: '>=',
      unit: 'percentage',
      measurementFrequency: 'monthly',
      dataSource: 'manual',
      currentValue: '',
      lastMeasuredAt: '',
      trend: 'stable',
      notes: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const openEdit = (m) => {
    setForm({
      processId: m.processId || '',
      processCode: m.processCode || '',
      processName: m.processName || '',
      name: m.name || '',
      metricType: m.metricType || 'operational',
      targetValue: m.targetValue ?? '',
      targetOperator: m.targetOperator || '>=',
      unit: m.unit || 'percentage',
      measurementFrequency: m.measurementFrequency || 'monthly',
      dataSource: m.dataSource || 'manual',
      currentValue: m.currentValue ?? '',
      lastMeasuredAt: m.lastMeasuredAt ? (typeof m.lastMeasuredAt === 'string' ? m.lastMeasuredAt.slice(0, 10) : m.lastMeasuredAt.toISOString?.().slice(0, 10)) : '',
      trend: m.trend || 'stable',
      notes: m.notes || ''
    });
    setEditingId(m.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!organizationId || !userId) return;
    const std = standards.find((s) => s.id === form.processId);
    const payload = {
      organizationId,
      processId: form.processId || null,
      processCode: std?.processCode || form.processCode || null,
      processName: std?.name || form.processName || null,
      name: form.name,
      metricType: form.metricType,
      targetValue: form.targetValue === '' ? null : Number(form.targetValue),
      targetOperator: form.targetOperator,
      unit: form.unit,
      measurementFrequency: form.measurementFrequency,
      dataSource: form.dataSource,
      currentValue: form.currentValue === '' ? null : Number(form.currentValue),
      lastMeasuredAt: form.lastMeasuredAt || null,
      trend: form.trend,
      notes: form.notes
    };
    try {
      if (editingId) {
        await performanceMetricService.update(editingId, payload, userId);
      } else {
        await performanceMetricService.create(payload, userId);
      }
      resetForm();
      loadMetrics();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await performanceMetricService.delete(deleteConfirm);
      setDeleteConfirm(null);
      loadMetrics();
    } catch (err) {
      setError(err.message);
    }
  };

  const formatValue = (m) => {
    if (m.currentValue == null) return '—';
    if (m.unit === 'percentage') return `${m.currentValue}%`;
    if (m.unit === 'rate_per_1000') return `${m.currentValue} per 1000`;
    return String(m.currentValue);
  };

  const TrendIcon = ({ trend }) => {
    if (trend === 'improving') return <TrendingUp className="trend-icon improving" />;
    if (trend === 'declining') return <TrendingDown className="trend-icon declining" />;
    return <Minus className="trend-icon stable" />;
  };

  return (
    <div className="performance-metric-manager">
      <div className="pm-header">
        <h2>{t('qms:performanceMetrics') || 'Performance Metrics'}</h2>
        <div className="pm-actions">
          <select
            value={processFilter}
            onChange={(e) => setProcessFilter(e.target.value)}
            className="pm-filter"
          >
            <option value="">{t('qms:allStandards') || 'All standards'}</option>
            {standards.map((s) => (
              <option key={s.id} value={s.id}>
                {s.processCode} – {s.name}
              </option>
            ))}
          </select>
          <button type="button" className="btn btn-primary" onClick={() => { resetForm(); setShowForm(true); }}>
            <Plus size={18} /> {t('qms:addMetric') || 'Add metric'}
          </button>
        </div>
      </div>

      {error && <div className="pm-error">{error}</div>}

      {loading ? (
        <p>{t('qms:loading') || 'Loading…'}</p>
      ) : metrics.length === 0 ? (
        <div className="pm-empty">
          <p>{t('qms:noMetricsYet') || 'No performance metrics yet. Add metrics to track compliance, operational, clinical, or risk indicators.'}</p>
          <button type="button" className="btn btn-primary" onClick={() => setShowForm(true)}>
            <Plus size={18} /> {t('qms:addMetric') || 'Add metric'}
          </button>
        </div>
      ) : (
        <div className="pm-table-wrap">
          <table className="pm-table">
            <thead>
              <tr>
                <th>{t('qms:metricName') || 'Metric'}</th>
                <th>{t('qms:standard') || 'Standard'}</th>
                <th>{t('qms:type') || 'Type'}</th>
                <th>{t('qms:current') || 'Current'}</th>
                <th>{t('qms:target') || 'Target'}</th>
                <th>{t('qms:trend') || 'Trend'}</th>
                <th>{t('qms:status') || 'Status'}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {metrics.map((m) => (
                <tr key={m.id} className={m.belowTarget ? 'below-target' : ''}>
                  <td>{m.name}</td>
                  <td>{m.processCode || m.processName || '—'}</td>
                  <td>{t(`qms:metricType_${m.metricType}`) || m.metricType}</td>
                  <td>{formatValue(m)}</td>
                  <td>
                    {m.targetValue != null ? `${m.targetOperator} ${m.targetValue}${m.unit === 'percentage' ? '%' : m.unit === 'rate_per_1000' ? ' per 1000' : ''}` : '—'}
                  </td>
                  <td><TrendIcon trend={m.trend} /></td>
                  <td>
                    {m.belowTarget ? (
                      <span className="status-badge below"><AlertTriangle size={14} /> {t('qms:belowTarget') || 'Below target'}</span>
                    ) : (
                      <span className="status-badge on-target">✓ {t('qms:onTarget') || 'On target'}</span>
                    )}
                  </td>
                  <td>
                    <button type="button" className="btn-icon" onClick={() => openEdit(m)} title={t('qms:edit') || 'Edit'}>
                      <Edit size={16} />
                    </button>
                    <button type="button" className="btn-icon danger" onClick={() => setDeleteConfirm(m.id)} title={t('qms:delete') || 'Delete'}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="pm-modal-overlay" onClick={() => resetForm()}>
          <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingId ? (t('qms:editMetric') || 'Edit metric') : (t('qms:addMetric') || 'Add metric')}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <label>{t('qms:standard') || 'Standard'}</label>
                <select value={form.processId} onChange={(e) => setForm({ ...form, processId: e.target.value })}>
                  <option value="">—</option>
                  {standards.map((s) => (
                    <option key={s.id} value={s.id}>{s.processCode} – {s.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label>{t('qms:metricName') || 'Metric name'}</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="form-row two-col">
                <div>
                  <label>{t('qms:type') || 'Type'}</label>
                  <select value={form.metricType} onChange={(e) => setForm({ ...form, metricType: e.target.value })}>
                    {METRIC_TYPES.map((mt) => (
                      <option key={mt} value={mt}>{t(`qms:metricType_${mt}`) || mt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label>{t('qms:unit') || 'Unit'}</label>
                  <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                    {UNITS.map((u) => (
                      <option key={u} value={u}>{t(`qms:unit_${u}`) || u.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row two-col">
                <div>
                  <label>{t('qms:target') || 'Target'}</label>
                  <div className="target-input">
                    <select value={form.targetOperator} onChange={(e) => setForm({ ...form, targetOperator: e.target.value })} style={{ width: '60px' }}>
                      <option value=">=">≥</option>
                      <option value="<=">≤</option>
                      <option value=">">&gt;</option>
                      <option value="<">&lt;</option>
                      <option value="==">==</option>
                    </select>
                    <input type="number" step="any" value={form.targetValue} onChange={(e) => setForm({ ...form, targetValue: e.target.value })} placeholder="e.g. 80" />
                  </div>
                </div>
                <div>
                  <label>{t('qms:frequency') || 'Frequency'}</label>
                  <select value={form.measurementFrequency} onChange={(e) => setForm({ ...form, measurementFrequency: e.target.value })}>
                    {FREQUENCIES.map((f) => (
                      <option key={f} value={f}>{t(`qms:freq_${f}`) || f}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row two-col">
                <div>
                  <label>{t('qms:currentValue') || 'Current value'}</label>
                  <input type="number" step="any" value={form.currentValue} onChange={(e) => setForm({ ...form, currentValue: e.target.value })} placeholder="—" />
                </div>
                <div>
                  <label>{t('qms:lastMeasured') || 'Last measured'}</label>
                  <input type="date" value={form.lastMeasuredAt} onChange={(e) => setForm({ ...form, lastMeasuredAt: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <label>{t('qms:trend') || 'Trend'}</label>
                <select value={form.trend} onChange={(e) => setForm({ ...form, trend: e.target.value })}>
                  {TRENDS.map((tr) => (
                    <option key={tr} value={tr}>{t(`qms:trend_${tr}`) || tr}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label>{t('qms:dataSource') || 'Data source'}</label>
                <select value={form.dataSource} onChange={(e) => setForm({ ...form, dataSource: e.target.value })}>
                  {DATA_SOURCES.map((ds) => (
                    <option key={ds} value={ds}>{t(`qms:dataSource_${ds}`) || ds}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label>{t('qms:notes') || 'Notes'}</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>{t('qms:cancel') || 'Cancel'}</button>
                <button type="submit" className="btn btn-primary">{editingId ? (t('qms:save') || 'Save') : (t('qms:add') || 'Add')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="pm-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="pm-modal pm-confirm" onClick={(e) => e.stopPropagation()}>
            <p>{t('qms:confirmDeleteMetric') || 'Delete this metric?'}</p>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>{t('qms:cancel') || 'Cancel'}</button>
              <button type="button" className="btn btn-danger" onClick={handleDelete}>{t('qms:delete') || 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceMetricManager;
